// @ts-nocheck

import moment from 'moment';
import * as R from 'ramda';
import { CloudfrontService } from '../../../main/services/CloudfrontService/CloudfrontService';
import { DEFAULT_DATE_FORMAT } from '../../../main/support/dateUtils';
import type { DateRange } from '../../../main/types/dateRangeTypes';
import type { Query } from '../../../main/types/druidTypes';
import { AGENCY_VIEW } from '../viewConstants';
// TODO temporary, as to reduce refactoring noise,
// move common utils here and remove import
export * from '../../../main/support/utils';
export * from './druid/groupby';
export * from './druid/timeseries';
export * from './druid/distribution';
export * from './druid/transformers';
export * from './druid/cohorts';
export * from './druid/events';
export * from './druid/nativeQueries';
export * from './table_helpers';
export * from './eo_helpers';
export * from './eo_constants';
export * from './getWhitelistedErrorMessage';
export * from './buildColumn';

/**
 * serialize a given object's values as an array
 * @param  {object} value could be an object, array
 * @return {string}
 */
export function serialize(value: any, sort?: boolean | null) {
  return R.pipe(R.values, sort ? R.curry((v) => v.sort()) : R.identity, JSON.stringify)(value);
}

/**
 * serialize a given object's values as an array
 * by sorting the values provided in the object
 * @param  {object} value could be an object, array
 * @return {string}
 */
export function serializeWithSort(value: any) {
  return serialize(value, true);
}

/**
 * For a Druid query, makes sure the metric and aggregations are properly set
 * when unique is true
 * @param {Object} existing query
 * @param {boolean} whether unique is true or not
 * @return {Object} new query, containing metric and aggregations if unique
 */
export function applyUnique(query: Query, unique: boolean): Query {
  if (!unique) {
    return query;
  }

  const UNIQUE = 'unique_count';
  return R.merge(query, {
    metric: 'unique_count',
    aggregations: query.aggregations
      ? R.concat(
          // biome-ignore lint/suspicious/noDoubleEquals: ignored using `--suppress`
          query.aggregations.filter((a) => a != UNIQUE),
          [UNIQUE]
        )
      : [UNIQUE],
  });
}

export function applyWebOnly(query: Query, webOnly: boolean): Query {
  if (webOnly) query.webOnly = webOnly;

  return query;
}

export function alterQuery(query: Query, unique: boolean, webOnly: boolean): Query {
  let alteredQuery: Query = query;

  alteredQuery = applyUnique(alteredQuery, unique);
  alteredQuery = applyWebOnly(alteredQuery, webOnly);

  return alteredQuery;
}

export function coerceBooleanString(boolStr: string): boolean {
  return boolStr === 'true';
}

const isEKS = (host) => {
  return host.startsWith('dashboard-') && host.endsWith('stage.branch.io');
};

/**
 * Checks if the given host is a localhost environment.
 *
 * @param {string} host - The host URL to check.
 * @returns {boolean} - Returns `true` if the host contains 'localhost:' or '127.0.0.1:', otherwise `false`.
 */
const isLocalhost = (host: string): boolean => {
  return host.includes('localhost:') || host.includes('127.0.0.1:');
};

/**
 * Determines if the given host is on a subdomain.
 *
 * - Returns `false` for localhost and CloudFront URLs.
 * - Returns `true` for beta environments using `sso-` subdomains.
 * - Returns `false` for new EKS deployments (`dashboard-[name]` pattern).
 * - Assumes the user is on a subdomain if the first subdomain is not `dashboard`.
 *
 * @param {string} host - The host URL to check.
 * @returns {boolean} - Returns `true` if the host is considered a subdomain, otherwise `false`.
 */
export function isOnSubdomain(host: string): boolean {
  if (isLocalhost(host) || CloudfrontService.isCloudfrontUrl(`https://${host}`)) {
    return false;
  }

  const split = host.split('.');

  // not handling local development scenario since local testing doesn't use subdomains and requires mocking headers instead
  if (host.indexOf('dashboard.beta') > -1 || isEKS(host)) {
    // beta uses sso- to mimic a subdomain
    return split[0].indexOf('sso-') > -1;
  }

  // exclude new EKS deployments
  if (/^dashboard-[a-z]+/.test(split[0])) {
    return false;
  }

  // if the first subdomain is not dashboard, we can assume the user is on a subdomain
  return split[0] !== 'dashboard';
}

export function getDateRangeFromPreviousPeriod(dateRange: DateRange): DateRange {
  const { start_date: startDate, end_date: endDate } = dateRange;

  const period = new Date(endDate) - new Date(startDate) + 1;
  const prevStartDate = moment(new Date(startDate) - period).format(DEFAULT_DATE_FORMAT);
  const prevEndDate = moment(new Date(endDate) - period).format(DEFAULT_DATE_FORMAT);

  return {
    start_date: prevStartDate,
    end_date: prevEndDate,
  };
}
export function getQueryWithPreviousPeriod(query: Query): Query {
  const previousDateRange = getDateRangeFromPreviousPeriod({
    start_date: query.start_date,
    end_date: query.end_date,
  });
  return { ...query, ...previousDateRange };
}

/**
 * Takes an array of strings and turns it into a grammatically correct list,
 * including commas (and Oxford comma) and spaces.
 * @param {Array<string>}
 *      Ex 1: ['apples', 'oranges']
 *      Ex 2: ['apples', 'oranges', 'bananas']
 * @return {string}
 *      Ex 1: 'apples and oranges'
 *      Ex 2: 'apples, oranges, and bananas'
 */
export function createTextListFromArray(array: Array<string>): string {
  let text = array.pop();

  if (array.length) {
    text = `${array.join(', ')}${array.length > 1 ? ',' : ''} and ${text}`;
  }

  return text;
}
// biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
export function fillRange(start: number, end: number, step: number = 1): Array<number> {
  return (
    Array(end - start + 1)
      .fill()
      // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
      .map((v, ind) => start + ind)
  );
}
export const removeQueryParam = (url: string, param: string): string => {
  if (!url || !param) {
    return '';
  }

  const base = url.split('?')[0];
  const queryParams = url.split('?')[1];

  if (!queryParams) {
    return url;
  }

  const sanitizedParams = queryParams
    .split('&')
    .filter((p) => !p.includes(param))
    .join('&');

  if (!sanitizedParams) {
    return base;
  }

  return `${base}?${sanitizedParams}`;
};
export const generateDashDomain = (location: Record<string, any>): string => {
  const PATTERN_LOCAL = 'localhost';
  const PATTERN_BETA = '.beta.branch.io';
  const PATTERN_STAGING = '.stage.branch.io';
  const isNonProductionDomain = [PATTERN_LOCAL, PATTERN_BETA, PATTERN_STAGING].some((env) => location.href.includes(env));

  if (isNonProductionDomain) {
    return location.href;
  }

  return `${location.protocol}//dashboard.branch.io${location.pathname || ''}${location.search || ''}`;
};
export const findAgencyId = (appsAndOrgs: Array<any> | null | undefined): string | null | undefined => {
  const agency = appsAndOrgs && appsAndOrgs.find((org) => org.organization_type === AGENCY_VIEW);
  return agency && agency.organization_id;
};
export const dedupe = (arr: Array<string>) => {
  return arr.filter((item, index) => arr.indexOf(item) >= index);
};
// Consistent time for confirmations throughout the dashboard is 3 seconds
// i.e. message on successfully copying something to the clipboard
export const TIME_TO_DISPLAY_MESSAGE = 3000;
export const DEFAULT_DRUID_RECORD_LIMIT = 5000;
